import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CloudflareTurnstile from '../components/CloudflareTurnstile';

import SEO from '../components/SEO';
import '../styles/Auth.css';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, isAuthenticated, loading: authLoading, error: authError, clearError } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleTurnstileVerify = (token) => {
    setTurnstileToken(token);
    setError('');
  };

  const handleTurnstileError = () => {
    setTurnstileToken(null);
    setError('Verification failed. Please try again.');
  };

  const handleTurnstileExpire = () => {
    setTurnstileToken(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the verification');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await signIn(formData.email, formData.password, turnstileToken);

      if (result.success) {
        const from = location.state?.from;
        const redirectTo = from ? (from.pathname + (from.search || '')) : '/dashboard';
        navigate(redirectTo, { replace: true });
      } else {
        setError(result.error || 'Login failed. Please check your credentials.');
        setTurnstileToken(null);
        setTurnstileResetKey(prev => prev + 1);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setTurnstileToken(null);
      setTurnstileResetKey(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <SEO title="Login" description="Log in to your PathProxy dashboard to manage proxies, view bandwidth usage, and configure settings." path="/login" noindex={true} />
      <div className="auth-left">
        <div className="auth-logo">
          <Link to="/" className="auth-logo-link">
            <img src="/pathproxy.png" alt="PathProxy Logo" className="auth-logo-image" />
            <span className="brand-name">pathproxy</span>
          </Link>
        </div>

        <div className="auth-form-container">
          <h1>Get Started Now</h1>
          <p className="auth-subtitle">Enter your credentials to access your account</p>
          <p className="auth-switch">
            Don't have an account? <Link to="/signup">Signup</Link>
          </p>

          <div className="referral-banner">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <span>Refer friends and earn <strong>2%</strong> of their purchases. They get <strong>3% extra bandwidth</strong>!</span>
          </div>

          {(error || authError) && (
            <div className="auth-error">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              {error || authError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label>Email Address <span className="required">*</span></label>
              <div className="input-with-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  required
                  autoComplete="email"
                  maxLength={254}
                  disabled={loading || authLoading}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Password <span className="required">*</span></label>
              <div className="input-with-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  maxLength={128}
                  disabled={loading || authLoading}
                />
              </div>
            </div>

            <div className="captcha-box">
              <CloudflareTurnstile
                onVerify={handleTurnstileVerify}
                onError={handleTurnstileError}
                onExpire={handleTurnstileExpire}
                theme="dark"
                resetKey={turnstileResetKey}
              />
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading || authLoading || !turnstileToken}
            >
              {loading || authLoading ? (
                <>
                  <span className="btn-spinner"></span>
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </button>

            <Link to="/forgot-password" className="forgot-link">Forgot Password</Link>
          </form>
        </div>
      </div>

      <div className="auth-right">
      </div>
    </div>
  );
};

export default LoginPage;
